import type { JSX, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { IconButton } from './Button'

/**
 * Модальное окно на нативном `<dialog>`.
 *
 * Нативный элемент взят не из моды: он бесплатно даёт то, что руками пишут
 * неделю и всё равно с ошибками — top layer (модалка никогда не окажется под
 * соседней панелью из-за чужого z-index), ловушку фокуса, `Esc`, возврат фокуса
 * на триггер и `::backdrop`.
 *
 * Что добавлено руками:
 *  - пружинное появление и более быстрый выход (по правилу «выход короче входа»);
 *  - закрытие по клику в подложку — но не по клику в саму карточку;
 *  - `overflow` тела: длинная форма скроллится внутри модалки, а не растягивает
 *    её за пределы экрана.
 */
export interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  /** Пояснение под заголовком — одна строка, не абзац. */
  note?: string
  children: ReactNode
  /** Кнопки внизу. Первичное действие — последним, справа. */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const SIZE = {
  sm: 'max-w-[26rem]',
  md: 'max-w-[34rem]',
  lg: 'max-w-[48rem]'
} as const

export function Dialog({
  open,
  onClose,
  title,
  note,
  children,
  footer,
  size = 'md'
}: DialogProps): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const node = ref.current
    if (node === null) return
    if (open && !node.open) node.showModal()
    if (!open && node.open) node.close()
  }, [open])

  // `cancel` — это Esc. Отменяем дефолт и закрываем через наш обработчик, чтобы
  // состояние в React и состояние DOM не разошлись.
  useEffect(() => {
    const node = ref.current
    if (node === null) return
    const onCancel = (event: Event): void => {
      event.preventDefault()
      onClose()
    }
    node.addEventListener('cancel', onCancel)
    return () => node.removeEventListener('cancel', onCancel)
  }, [onClose])

  return (
    <dialog
      ref={ref}
      aria-label={title}
      onClick={event => {
        // Клик именно в подложку: цель события — сам <dialog>, а не его содержимое.
        if (event.target === ref.current) onClose()
      }}
      className={cn(
        // `m-auto` обязателен: нативный `<dialog>` центрируется браузером через
        // `margin: auto`, а preflight Tailwind v4 обнуляет margin у всех
        // элементов — из-за этого модалки прижимались к левому верхнему углу.
        'm-auto w-[calc(100vw-2rem)] rounded-lg border border-line-strong bg-panel p-0 text-ink shadow-lg',
        'backdrop:bg-abyss/70 backdrop:backdrop-blur-sm',
        'open:anim-modal open:backdrop:anim-backdrop',
        SIZE[size]
      )}
    >
      <div className="flex max-h-[85dvh] flex-col">
        <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate font-display text-md font-semibold text-ink">{title}</h2>
            {note === undefined ? null : <p className="mt-0.5 text-2xs text-ink-muted">{note}</p>}
          </div>
          <IconButton label="Закрыть" size="sm" onClick={onClose}>
            <X aria-hidden className="size-4" />
          </IconButton>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer === undefined ? null : (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  )
}
